/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

export type EnhanceSpeechTrackId = string;

// Keep in sync with enhance_speech_track_error_type.rb
export enum EnhanceSpeechTrackErrorType {
  FILE_SIZE_LIMIT = "file_size_limit",
  FILE_DURATION_LIMIT = "file_duration_limit",
  RATE_LIMIT = "rate_limit",
  CONCURRENCY_LIMIT = "concurrency_limit",
  CUMULATIVE_DURATION_LIMIT = "cumulative_duration_limit",
  INCALCULABLE_DURATION = "incalculable_duration",
  PROCESSING_TIMEOUT = "processing_timeout",
  UNSUPPORTED_FORMAT = "unsupported_format",
  UNSUPPORTED_EXTENSION = "unsupported_extension", // This can also represent an unsupported content type
  UNKNOWN_ERROR = "unknown_error",
  MULTI_FILE_LIMIT = "multi_file_limit",
  VIDEO_NOT_ACCEPTED = "video_not_accepted",
  DEPRECATED_SENSEI_TIMEOUT_EXCEPTION = "SenseiTimeoutException",
  DEPRECATED_SENSEI_UNSUPPORTED_FORMAT_EXCEPTION = "SenseiUnsupportedFormatException",
  DEPRECATED_SENSEI_EXCEPTION = "SenseiException",
  AUTH_EXCEPTION = "auth_exception",
  UPLOAD_FAILED = "upload_failed",
}

export type LimitReachedEnhanceSpeechTrackErrorType =
  | EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT
  | EnhanceSpeechTrackErrorType.FILE_DURATION_LIMIT
  | EnhanceSpeechTrackErrorType.CONCURRENCY_LIMIT
  | EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT
  | EnhanceSpeechTrackErrorType.MULTI_FILE_LIMIT
  | EnhanceSpeechTrackErrorType.VIDEO_NOT_ACCEPTED;

export const isLimitReachedError: {
  [T in EnhanceSpeechTrackErrorType]: boolean;
} = {
  [EnhanceSpeechTrackErrorType.FILE_SIZE_LIMIT]: true,
  [EnhanceSpeechTrackErrorType.FILE_DURATION_LIMIT]: true,
  [EnhanceSpeechTrackErrorType.CONCURRENCY_LIMIT]: true,
  [EnhanceSpeechTrackErrorType.CUMULATIVE_DURATION_LIMIT]: true,
  [EnhanceSpeechTrackErrorType.MULTI_FILE_LIMIT]: true,
  [EnhanceSpeechTrackErrorType.VIDEO_NOT_ACCEPTED]: true,
  [EnhanceSpeechTrackErrorType.RATE_LIMIT]: false,
  [EnhanceSpeechTrackErrorType.INCALCULABLE_DURATION]: false,
  [EnhanceSpeechTrackErrorType.PROCESSING_TIMEOUT]: false,
  [EnhanceSpeechTrackErrorType.UNSUPPORTED_FORMAT]: false,
  [EnhanceSpeechTrackErrorType.UNSUPPORTED_EXTENSION]: false,
  [EnhanceSpeechTrackErrorType.UNKNOWN_ERROR]: false,
  [EnhanceSpeechTrackErrorType.DEPRECATED_SENSEI_TIMEOUT_EXCEPTION]: false,
  [EnhanceSpeechTrackErrorType.DEPRECATED_SENSEI_UNSUPPORTED_FORMAT_EXCEPTION]:
    false,
  [EnhanceSpeechTrackErrorType.DEPRECATED_SENSEI_EXCEPTION]: false,
  [EnhanceSpeechTrackErrorType.AUTH_EXCEPTION]: false,
  [EnhanceSpeechTrackErrorType.UPLOAD_FAILED]: false,
};

// Representation of a file a user uploads for enhance as stored in our DB
export interface EnhanceSpeechTrack {
  id: EnhanceSpeechTrackId;
  trackName: string;
  originalAudioUrl: string | null;
  enhanceSpeechAudioUrl: string | null;
  mergedMediaUrl: string | null;
  errorType: EnhanceSpeechTrackErrorType | null;
  error?: Error;
  startedProcessingAt: string | null;
  finishedProcessingAt: string | null;
  duration: number | null;
  createdAt?: string;
  isVideo: boolean | null;
  limits: TrackLimited | null;
  modelVersion: ModelVersion;
}

type TrackLimited =
  | {
      isLimited: true;
      retryAtTime: string | null;
    }
  | {
      isLimited: false;
      retryAtTime: null;
    };

export interface CompletedEnhanceSpeechTrack extends EnhanceSpeechTrack {
  mergedMediaUrl: string;
  enhanceSpeechAudioUrl: string;
  originalAudioUrl: string;
  finishedProcessingAt: string;
}
